import type { FlockEntry } from "./DailyReportPage";
import type { FeedFormula } from "@/types/models";

interface Props {
  flocks: FlockEntry[];
  formulas: FeedFormula[];
  onChange: (index: number, field: keyof FlockEntry, value: string) => void;
  // True when the operator is editing an earlier day (existing daily-log
  // entries were pre-filled on step 2). Feed production isn't pre-filled
  // because a batch is a point-in-time event, not a daily total — but
  // blank fields without a cue look like "did we lose yesterday's batch?"
  editMode?: boolean;
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const selectClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function StepFeedPrepared({ flocks, formulas, onChange, editMode }: Props) {
  return (
    <div>
      <h4 className="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
        Feed Prepared (Stock Out)
      </h4>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        How much feed was prepared for each flock today?
      </p>
      {editMode && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400">
          Editing an earlier day: any feed-production entries already saved
          for this date stay as-is. Leave fields blank to skip, or enter a
          value to record a NEW feed-production batch on top of what's there.
        </div>
      )}
      <div className="space-y-4">
        {flocks.map((f, i) => (
          <div
            key={f.flock_id}
            className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
          >
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                {f.shed_code || f.flock_type}
              </h5>
              <span className="text-xs text-gray-500 dark:text-gray-400">{f.batch_name}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Feed Prepared (kg)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={f.feed_prepared_kg}
                  onChange={(e) => onChange(i, "feed_prepared_kg", e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Feed Formula{parseFloat(f.feed_prepared_kg) > 0 ? " *" : ""}
                </label>
                <select
                  value={f.formula_id}
                  onChange={(e) => onChange(i, "formula_id", e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select formula</option>
                  {formulas.map((fm) => (
                    <option key={fm.id} value={fm.id}>
                      {fm.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
